/**
 * ReportStats - Animated stat counters for the report overview.
 * Counts up on scroll-into-view using IntersectionObserver.
 */
import React, { useState, useEffect, useRef } from 'react';
import { FileText } from 'lucide-react';
import { Section, SectionTitle, StatsGrid, StatCard, StatValue, StatLabel } from './reportStyles';
import { useTranslation } from '../../hooks/useTranslation.jsx';

function AnimatedNumber({ value, duration = 1000 }) {
  const [display, setDisplay] = useState(0);
  const ref = useRef(null);
  const animated = useRef(false);

  useEffect(() => {
    if (!ref.current || animated.current) return;

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !animated.current) {
        animated.current = true;
        const start = performance.now();
        const num = typeof value === 'number' ? value : parseInt(value, 10) || 0;

        const tick = (now) => {
          const elapsed = now - start;
          const progress = Math.min(elapsed / duration, 1);
          // ease-out
          const eased = 1 - Math.pow(1 - progress, 3);
          setDisplay(Math.round(eased * num));
          if (progress < 1) requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);
      }
    }, { threshold: 0.5 });

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, duration]);

  return <span ref={ref}>{display}</span>;
}

// Sitra brand: full-saturation Sitra colors, sharp blocks. No soft tints.
// Colors carry semantic weight (red=obstacle, green=opportunity).
const STAT_CONFIG = [
  { key: 'participants',  labelKey: 'report.stats.participants',  bg: '#FFD93D', color: '#000' }, // Sitra yellow
  { key: 'submissions',   labelKey: 'report.stats.submissions',   bg: '#5DCEDB', color: '#000' }, // Sitra cyan
  { key: 'scenarios',     labelKey: 'report.stats.scenarios',     bg: '#fa6129', color: '#000' }, // Sitra orange
  { key: 'pivotPoints',   labelKey: 'report.stats.pivotPoints',   bg: '#367ABA', color: '#fff' }, // Sitra blue
  { key: 'obstacles',     labelKey: 'report.stats.obstacles',     bg: '#fa6129', color: '#fff' }, // Sitra Puolukka
  { key: 'opportunities', labelKey: 'report.stats.opportunities', bg: '#00613d', color: '#fff' }, // Sitra green
  { key: 'solutions',     labelKey: 'report.stats.solutions',     bg: '#000',    color: '#FFD93D' },
];

export default function ReportStats({ stats, id = 'stats', title }) {
  const { t } = useTranslation();
  const sectionTitle = title || t('report.stats.title');
  // Only show stats that have a value > 0
  const visibleStats = STAT_CONFIG.filter(s => (stats[s.key] || 0) > 0);

  if (visibleStats.length === 0) return null;

  return (
    <Section id={id}>
      <SectionTitle>{sectionTitle}</SectionTitle>
      <StatsGrid>
        {visibleStats.map(s => (
          <StatCard key={s.key} $bg={s.bg}>
            <StatValue $color={s.color}>
              <AnimatedNumber value={stats[s.key]} />
            </StatValue>
            <StatLabel style={{ color: s.color, opacity: 0.85 }}>{t(s.labelKey)}</StatLabel>
          </StatCard>
        ))}
      </StatsGrid>
    </Section>
  );
}
